/**
 * One project on the Dashboard's card grid. SPEC-0007 Recommendation §3.
 *
 * Leads with exposure — the dollar figure is what a client actually cares
 * about first — then severity, where the discrepancy is, and how on-budget
 * the project is. Every money and severity rule here (unscored isn't zero,
 * exact and estimated never summed) mirrors `RunSummaryStats` and
 * `RunDetail`, because this card is a portfolio-level view onto the exact
 * same findings, not a new representation of them.
 */
import { Link } from 'react-router-dom';
import type { Project } from '@picklii/shared';
import { EditProjectDatesDialog } from './EditProjectDatesDialog';
import { Icon } from './Icon';
import {
	bandClass,
	bandProgressClass,
	bandStripeClass,
	FINDING_LABELS,
	money,
	percent,
	shortDate,
	signedHours,
} from '../lib/format';

export function ProjectCard({
	project,
	busyAction,
	onArchiveToggle,
	onSaveDates,
}: {
	project: Project;
	/** Which of this card's own actions is currently in flight, if any — so
	 * only the button that triggered it shows a spinner, and both are
	 * disabled while either is running. */
	busyAction?: 'archive' | 'dates';
	onArchiveToggle(): void;
	onSaveDates(dates: { goLiveDate: string | null; endDate: string | null }): void;
}) {
	const hasExposure = project.latestExactExposureCents > 0 || project.latestEstimatedExposureCents > 0;
	const dateDisputed = project.goLiveDateDisputed || project.endDateDisputed;
	const busy = busyAction !== undefined;

	return (
		<div
			className={`card bg-base-200 border-base-300 border border-l-4 ${bandStripeClass(project.latestSeverityBand)} shadow-xs`}>
			<div className="card-body gap-3">
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<h3 className="truncate text-base font-semibold">{project.clientName || project.projectId}</h3>
						<p className="text-base-content/60 truncate text-xs">{project.projectId}</p>
					</div>

					<div
						className={`badge badge-sm shrink-0 ${dateDisputed ? 'badge-warning' : 'badge-ghost'}`}
						title={
							dateDisputed
								? 'Uploads disagreed on a lifecycle date — resolve it in Edit dates'
								: undefined
						}>
						{project.endDate !== null ? `Ends ${shortDate(project.endDate)}` : 'No end date'}
						{dateDisputed && <Icon name="warning" className="ms-1 size-3" />}
					</div>
				</div>

				{project.goLiveDate !== null && (
					<p className="text-base-content/60 -mt-2 text-xs">Live since {shortDate(project.goLiveDate)}</p>
				)}

				<div>
					{hasExposure ? (
						<div className="flex flex-col">
							{project.latestExactExposureCents > 0 && (
								<span className="text-2xl font-semibold tabular-nums">
									{money(project.latestExactExposureCents)}
								</span>
							)}
							{project.latestEstimatedExposureCents > 0 && (
								<span
									className={
										project.latestExactExposureCents > 0
											? 'text-base-content/70 text-xs tabular-nums'
											: 'text-2xl font-semibold tabular-nums'
									}>
									{project.latestExactExposureCents > 0 ? '+ ' : ''}
									{money(project.latestEstimatedExposureCents)} estimated
								</span>
							)}
						</div>
					) : (
						<span className="text-base-content/70 text-sm">No scored exposure</span>
					)}
				</div>

				<div className="flex flex-wrap items-center gap-2">
					{project.latestFindingType !== null ? (
						<span className={`badge badge-sm ${bandClass(project.latestSeverityBand)}`}>
							{project.latestSeverityBand ?? 'Not scored'}
						</span>
					) : (
						<span className="badge badge-sm badge-success badge-outline">No findings</span>
					)}
					{project.latestFindingType !== null && (
						<span className="text-base-content/70 text-xs">
							{FINDING_LABELS[project.latestFindingType]}
						</span>
					)}
					{project.hasUnmatchedRoles && (
						<span className="badge badge-sm badge-warning badge-outline gap-1">
							<Icon name="warning" className="size-3" />
							Unmatched roles
						</span>
					)}
				</div>

				<div>
					{project.latestBurnPct !== null ? (
						<>
							<div className="text-base-content/70 flex justify-between text-xs">
								<span>Burn {percent(project.latestBurnPct)}</span>
								<span>Δ {signedHours(project.latestDeltaHours ?? 0)} hrs</span>
							</div>
							<progress
								className={`progress w-full ${bandProgressClass(project.latestSeverityBand)}`}
								value={Math.round(Math.min(1, Math.max(0, project.latestBurnPct)) * 100)}
								max={100}
							/>
						</>
					) : (
						<p className="text-base-content/60 text-xs">No budget to burn against</p>
					)}
				</div>

				<div className="card-actions mt-1 items-center justify-between">
					<Link to={`/runs/${project.latestRunId}`} className="link text-sm">
						View latest run
					</Link>
					<div className="flex gap-2">
						<EditProjectDatesDialog
							project={project}
							busy={busyAction === 'dates'}
							onSave={onSaveDates}
						/>
						<button className="btn btn-sm btn-outline" disabled={busy} onClick={onArchiveToggle}>
							{busyAction === 'archive' ? (
								<span className="loading loading-spinner loading-xs" />
							) : project.archived ? (
								'Restore'
							) : (
								'Archive'
							)}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
}
